import { useState, useEffect } from 'react'
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  BanknotesIcon,
  ClockIcon,
  ArrowTopRightOnSquareIcon
} from '@heroicons/react/24/outline'
import { CheckCircleIcon as CheckCircleSolid } from '@heroicons/react/24/solid'
import { ConnectedAccount, createConnectedAccount, getOnboardingLink, getAccountStatus } from '../utils/stripeConnect'

interface ProfessionalOnboardingProps {
  professionalId: string
  email: string
  onOnboardingComplete?: () => void
}

export default function ProfessionalOnboarding({
  professionalId,
  email,
  onOnboardingComplete
}: ProfessionalOnboardingProps) {
  const [account, setAccount] = useState<ConnectedAccount | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)

  useEffect(() => {
    checkAccountStatus()
  }, [professionalId])

  const checkAccountStatus = async () => {
    try {
      setLoading(true)
      const accountStatus = await getAccountStatus(professionalId)
      setAccount(accountStatus)
    } catch (err: any) {
      setError(err.message || 'Failed to check account status')
    } finally {
      setLoading(false)
    }
  }

  const createAccount = async () => {
    try {
      setCreating(true)
      setError(null)

      const newAccount = await createConnectedAccount(professionalId, email)
      setAccount(newAccount)

      // Automatically redirect to onboarding
      if (newAccount.onboardingUrl) {
        window.open(newAccount.onboardingUrl, '_blank')
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create account')
    } finally {
      setCreating(false)
    }
  }

  const continueOnboarding = async () => {
    try {
      const returnUrl = `${window.location.origin}/app?onboarding=success`
      const refreshUrl = `${window.location.origin}/app?onboarding=refresh`

      const onboardingUrl = await getOnboardingLink(professionalId, returnUrl, refreshUrl)
      window.open(onboardingUrl, '_blank')
    } catch (err: any) {
      setError(err.message || 'Failed to get onboarding link')
    }
  }

  const getAccountStatusInfo = () => {
    if (!account) return null

    if (account.chargesEnabled && account.payoutsEnabled) {
      return {
        status: 'complete',
        title: 'Payment Setup Complete! 🎉',
        description: 'You can now receive payments from clients.',
        color: 'green'
      }
    }

    if (account.detailsSubmitted) {
      return {
        status: 'review',
        title: 'Under Review',
        description: 'Stripe is reviewing your information. This usually takes 1-2 business days.',
        color: 'yellow'
      }
    }

    return {
      status: 'incomplete',
      title: 'Setup Required',
      description: 'Complete your payment account setup to start receiving payments.',
      color: 'red'
    }
  }

  if (loading) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="h-6 bg-gray-200 rounded w-1/2 mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
        </div>
      </div>
    )
  }

  const statusInfo = getAccountStatusInfo()

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="flex items-center mb-6">
        <BanknotesIcon className="h-8 w-8 text-indigo-600 mr-3" />
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Payment Account Setup</h2>
          <p className="text-sm text-gray-600">Configure your account to receive payments from clients</p>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400 mt-0.5 mr-2" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      )}

      {!account ? (
        // No account created yet
        <div className="text-center py-8">
          <div className="mx-auto w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mb-4">
            <BanknotesIcon className="h-8 w-8 text-indigo-600" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Set Up Payment Account
          </h3>
          <p className="text-gray-600 mb-6 max-w-md mx-auto">
            Create your payment account with Stripe to start receiving payments from clients.
            This is secure and takes just a few minutes.
          </p>
          <button
            onClick={createAccount}
            disabled={creating}
            className="inline-flex items-center px-6 py-3 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {creating ? (
              <>
                <div className="animate-spin -ml-1 mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                Creating Account...
              </>
            ) : (
              <>
                <BanknotesIcon className="h-5 w-5 mr-2" />
                Create Payment Account
              </>
            )}
          </button>
        </div>
      ) : (
        // Account exists, show status
        <div>
          <div className="mb-6">
            <div className={`flex items-center p-4 rounded-lg ${
              statusInfo?.color === 'green' ? 'bg-green-50 border border-green-200' :
              statusInfo?.color === 'yellow' ? 'bg-yellow-50 border border-yellow-200' :
              'bg-red-50 border border-red-200'
            }`}>
              {statusInfo?.status === 'complete' ? (
                <CheckCircleSolid className="h-6 w-6 text-green-600 mr-3" />
              ) : statusInfo?.status === 'review' ? (
                <ClockIcon className="h-6 w-6 text-yellow-600 mr-3" />
              ) : (
                <ExclamationTriangleIcon className="h-6 w-6 text-red-600 mr-3" />
              )}
              <div>
                <h3 className={`font-medium ${
                  statusInfo?.color === 'green' ? 'text-green-900' :
                  statusInfo?.color === 'yellow' ? 'text-yellow-900' :
                  'text-red-900'
                }`}>
                  {statusInfo?.title}
                </h3>
                <p className={`text-sm ${
                  statusInfo?.color === 'green' ? 'text-green-700' :
                  statusInfo?.color === 'yellow' ? 'text-yellow-700' :
                  'text-red-700'
                }`}>
                  {statusInfo?.description}
                </p>
              </div>
            </div>
          </div>

          {/* Account Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="flex items-center">
              {account.chargesEnabled ? (
                <CheckCircleSolid className="h-5 w-5 text-green-500 mr-2" />
              ) : (
                <div className="h-5 w-5 border-2 border-gray-300 rounded-full mr-2"></div>
              )}
              <span className={account.chargesEnabled ? 'text-green-700' : 'text-gray-600'}>
                Accept Payments
              </span>
            </div>

            <div className="flex items-center">
              {account.payoutsEnabled ? (
                <CheckCircleSolid className="h-5 w-5 text-green-500 mr-2" />
              ) : (
                <div className="h-5 w-5 border-2 border-gray-300 rounded-full mr-2"></div>
              )}
              <span className={account.payoutsEnabled ? 'text-green-700' : 'text-gray-600'}>
                Receive Payouts
              </span>
            </div>

            <div className="flex items-center">
              {account.detailsSubmitted ? (
                <CheckCircleSolid className="h-5 w-5 text-green-500 mr-2" />
              ) : (
                <div className="h-5 w-5 border-2 border-gray-300 rounded-full mr-2"></div>
              )}
              <span className={account.detailsSubmitted ? 'text-green-700' : 'text-gray-600'}>
                Identity Verified
              </span>
            </div>

            <div className="flex items-center">
              {account.requirementsCurrentlyDue.length === 0 ? (
                <CheckCircleSolid className="h-5 w-5 text-green-500 mr-2" />
              ) : (
                <div className="h-5 w-5 border-2 border-gray-300 rounded-full mr-2"></div>
              )}
              <span className={account.requirementsCurrentlyDue.length === 0 ? 'text-green-700' : 'text-gray-600'}>
                Setup Complete
              </span>
            </div>
          </div>

          {/* Requirements */}
          {account.requirementsCurrentlyDue.length > 0 && (
            <div className="mb-6">
              <h4 className="text-sm font-medium text-gray-900 mb-2">Required Information:</h4>
              <ul className="text-sm text-gray-600 space-y-1">
                {account.requirementsCurrentlyDue.map((requirement) => (
                  <li key={requirement} className="flex items-center">
                    <div className="h-1.5 w-1.5 bg-red-400 rounded-full mr-2"></div>
                    {requirement.replace('individual.', '').replace('_', ' ')}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            {!account.chargesEnabled || !account.payoutsEnabled ? (
              <button
                onClick={continueOnboarding}
                className="inline-flex items-center justify-center px-6 py-3 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors"
              >
                <ArrowTopRightOnSquareIcon className="h-5 w-5 mr-2" />
                Complete Setup
              </button>
            ) : (
              <button
                onClick={() => onOnboardingComplete?.()}
                className="inline-flex items-center justify-center px-6 py-3 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors"
              >
                <CheckCircleIcon className="h-5 w-5 mr-2" />
                Setup Complete
              </button>
            )}

            {account.dashboardUrl && (
              <button
                onClick={() => window.open(account.dashboardUrl, '_blank')}
                className="inline-flex items-center justify-center px-6 py-3 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors"
              >
                <ArrowTopRightOnSquareIcon className="h-5 w-5 mr-2" />
                View Stripe Dashboard
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}